/**
 * Ctrl+K search — pure ranking over campaign state (Phase 2: GM Core).
 * Source content (Phase 3) will plug in as an extra candidate source later.
 */
import type { Campaign } from '@/domain/campaign';
import type { Locale } from '@/domain/shared';
import { localized, translate } from '@/i18n';
import type { RouteId } from '@/state/store';
import { ROUTE_FOR_KIND, type EntityKind } from './labels';
import { isGmUnlocked } from '@/persistence/gm-lock';

export interface SearchResult {
  kind: EntityKind;
  id: string;
  route: RouteId;
  title: string;
  /** Matched context around a body hit; null when the title matched. */
  snippet: string | null;
}

interface Candidate {
  kind: EntityKind;
  id: string;
  title: string;
  body: string[];
}

function collect(campaign: Campaign, locale: Locale): Candidate[] {
  const out: Candidate[] = [];
  out.push({
    kind: 'campaign',
    id: campaign.id,
    title: campaign.name,
    body: [campaign.era ?? '', campaign.synopsis],
  });
  for (const s of campaign.sessions) {
    const title = localized(s.title, locale);
    out.push({
      kind: 'session',
      id: s.id,
      title: `#${s.number} · ${title || translate(locale, 'sessions.untitled')}`,
      body: [title, s.summary, s.plannedContent, s.advancementNotes, s.date ?? ''],
    });
  }
  for (const c of campaign.characters) {
    out.push({
      kind: 'character',
      id: c.id,
      title: c.name,
      body: [c.concept, c.playerName ?? '', c.campaignNotes],
    });
  }
  for (const n of campaign.npcs) {
    out.push({
      kind: 'npc',
      id: n.id,
      title: n.name || translate(locale, 'npcs.untitled'),
      body: [n.concept, ...n.secrets, n.statblockRef?.text ?? ''],
    });
  }
  for (const e of campaign.encounters) {
    out.push({ kind: 'encounter', id: e.id, title: e.name, body: [e.purpose, e.rewardNotes, ...e.obstacles] });
  }
  for (const l of campaign.locations) {
    out.push({ kind: 'location', id: l.id, title: l.name, body: [l.kind, l.description, l.notes] });
  }
  for (const f of campaign.factions) {
    out.push({ kind: 'faction', id: f.id, title: f.name, body: [f.goal, f.influence, f.notes, ...f.notableMembers] });
  }
  for (const i of campaign.items) {
    out.push({
      kind: 'item',
      id: i.id,
      title: localized(i.name, locale) || translate(locale, 'items.untitled'),
      body: [localized(i.description, locale), i.kind],
    });
  }
  // Story nodes are behind the GM password (src/persistence/gm-lock.ts): the
  // search must not read them out while the story section is locked.
  for (const s of isGmUnlocked() ? campaign.storyNodes : []) {
    out.push({ kind: 'story-node', id: s.id, title: s.title, body: [s.summary, s.status] });
  }
  for (const n of campaign.notes) {
    out.push({
      kind: 'note',
      id: n.id,
      title: n.title || translate(locale, 'notes.untitled'),
      body: [n.body, ...n.tags],
    });
  }
  return out;
}

function snippetAround(text: string, index: number, length: number): string {
  const start = Math.max(0, index - 30);
  const end = Math.min(text.length, index + length + 50);
  const core = text.slice(start, end).replace(/\s+/g, ' ').trim();
  return `${start > 0 ? '…' : ''}${core}${end < text.length ? '…' : ''}`;
}

/**
 * Case-insensitive substring search: title hits rank above body hits,
 * both ranked alphabetically within their group. Empty query → no results
 * (the palette shows its hint instead).
 */
export function searchCampaign(
  campaign: Campaign,
  query: string,
  locale: Locale,
  limit = 30,
): SearchResult[] {
  const q = query.trim().toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US');
  if (!q) return [];

  const titleHits: SearchResult[] = [];
  const bodyHits: SearchResult[] = [];

  for (const c of collect(campaign, locale)) {
    if (!c.title.trim()) continue;
    if (c.title.toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US').includes(q)) {
      titleHits.push({ kind: c.kind, id: c.id, route: ROUTE_FOR_KIND[c.kind], title: c.title, snippet: null });
      continue;
    }
    for (const field of c.body) {
      if (!field) continue;
      const idx = field.toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US').indexOf(q);
      if (idx >= 0) {
        bodyHits.push({
          kind: c.kind,
          id: c.id,
          route: ROUTE_FOR_KIND[c.kind],
          title: c.title,
          snippet: snippetAround(field, idx, q.length),
        });
        break; // one snippet per entity is enough
      }
    }
  }

  const collator = new Intl.Collator(locale === 'tr' ? 'tr-TR' : 'en-US');
  titleHits.sort((a, b) => collator.compare(a.title, b.title));
  bodyHits.sort((a, b) => collator.compare(a.title, b.title));
  return [...titleHits, ...bodyHits].slice(0, limit);
}
